// Modules
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Components
import { Button, Menu, Spinner } from '@/ui';
import { Sidebar } from '../../components';
import {
  StyledA,
  StyledButtonsWrapper,
  StyledLayout,
  StyledLinkWrap,
  StyledLogo,
  StyledLogoWrap,
  StyledTitle,
  StyledTitleModal,
  StylesLogoOut,
} from './MainLayoutStyles';
import MainLayoutContent from '../MainLayoutContent/MainLayoutContent';
import { LinkOutlined } from '@ant-design/icons';

// Modules
import { logout, useUnauthorizedRedirect } from '@/modules/Auth';
import { getAdminProfile, selectAdminProfile, selectAuthIsLoading } from '@/modules/AdminProfile';
import { getNewFeedbacksCount, selectNewFeedbacksCount } from '@/modules/Feedbacks';
import { getNewOrdersCount, selectNewOrdersCount } from '@/modules/Orders';
import { usePermissions } from '@/modules/Permissions';

// Helpers
import { getMenuItemsConfig } from '../../helpers/getMenuItemsConfig';
import { env } from '@/config/env';
import { useModal } from '@/hooks';
import { Modal, ModalSize } from '@/components';

const COUNT_POLLING_INTERVAL = 60_000;

const MainLayout = () => {
  const { t } = useTranslation(['common']);
  useUnauthorizedRedirect({});
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useAppState(selectAuthIsLoading);
  const profile = useAppState(selectAdminProfile);
  const newFeedbacksCount = useAppState(selectNewFeedbacksCount);
  const newOrdersCount = useAppState(selectNewOrdersCount);
  const { canView } = usePermissions();
  const [selectedKey, setSelectedKey] = useState(location.pathname);
  const { isOpened, openModal, closeModal } = useModal();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const onClick: MenuProps['onClick'] = (e) => {
    // Parent submenu keys are not routes — only leaf items navigate.
    if (e.key === 'dashboard-index' || e.key === 'import-index' || e.key === '5') {
      return;
    }
    setSelectedKey(e.key);
    navigate(e.key);
  };

  const dispatch = useAppDispatch();
  const handleLogout = () => {
    dispatch(logout());
  };

  useEffect(() => {
    if (!profile && !isLoading) {
      const fetchAdminData = async () => {
        await dispatch(getAdminProfile());
      };
      fetchAdminData();
    }
  }, [isLoading, profile]);

  useEffect(() => {
    setSelectedKey(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!profile) return;

    dispatch(getNewFeedbacksCount());
    dispatch(getNewOrdersCount());

    pollingRef.current = setInterval(() => {
      dispatch(getNewFeedbacksCount());
      dispatch(getNewOrdersCount());
    }, COUNT_POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [dispatch, profile]);

  return (
    <StyledLayout>
      <Sidebar>
        <StyledLogoWrap>
          <StyledLogo src="/favicon.ico" alt="Favicon" />
          <StyledTitle>{t('name')}</StyledTitle>
        </StyledLogoWrap>
        <Menu
          mode="vertical"
          onClick={onClick}
          items={getMenuItemsConfig(t, canView, newFeedbacksCount, newOrdersCount)}
          selectedKeys={[selectedKey || '/']}
        />
        <StyledLinkWrap>
          <StyledA href={`${env.WEBSITE_URL}`} target="_blank" rel="noreferrer">
            <LinkOutlined />
            {t('web_site_redirect')}
          </StyledA>
        </StyledLinkWrap>
        <StylesLogoOut onClick={openModal}>{t('log_out')}</StylesLogoOut>
      </Sidebar>
      <MainLayoutContent>
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </MainLayoutContent>

      <Modal visible={isOpened} onCancel={closeModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('account_logout')}</StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={closeModal} loading={isLoading}>
            {t('stay_in_system')}
          </Button>
          <Button onClick={handleLogout} loading={isLoading} type="text">
            {t('log_out')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
    </StyledLayout>
  );
};

export default MainLayout;
